import type { DataGridCheckRow } from '../../components/DataGrid/CheckCell/CheckCell';
import type {
  ContentObject,
  ContentTypeDefinition,
} from '../../lib/flotiq-client/types/api-response';

import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { showValidationErrorToast } from '../../form-hooks/lib/helpers';
import { duplicateContentObject } from '../../lib/flotiq-client';
import {
  parseResponseErrors,
  ResponseError,
} from '../../lib/flotiq-client/response-errors';
import { getTestProps } from '../../lib/helpers';

import Loader from '../../components/Loader/Loader';

import { useModals } from '../../contexts/ModalContext';

import DuplicateRelationsForm from '../../form/DuplicateRelationsForm/DuplicateRelationsForm';
import useSelectedSpace from '../useSelectedSpace';
import useToken from '../useToken';
import { collectDuplicableRelations } from './relations-extractor';

export const useDuplicateModal = (
  contentType?: ContentTypeDefinition | null,
  testId?: string,
) => {
  const { t } = useTranslation();
  const jwt = useToken();
  const { space, buildUrlWithSpace } = useSelectedSpace();
  const modal = useModals();
  const navigate = useNavigate();

  const generateUrl = (id: string, action: 'edit' | 'duplicate') => {
    const contentTypeName = contentType?.name || '';
    if (contentTypeName === '_webhooks') {
      return buildUrlWithSpace(`webhooks/${action}/${id}`);
    }
    return buildUrlWithSpace(
      `content-type-objects/${action}/${contentTypeName}/${id}`,
    );
  };

  const duplicateObject = async (
    contentTypeName: string,
    id: DataGridCheckRow,
    idsToDuplicate: string[],
  ): Promise<[Record<string, string | string[]> | undefined, boolean]> => {
    try {
      const { body, status, ok } = await duplicateContentObject(jwt, space, {
        contentTypeName,
        id,
        relatedObjectsToDuplicate: idsToDuplicate,
      });

      if (!ok) {
        throw parseResponseErrors(body, status);
      }
      navigate(generateUrl(body.id, 'edit'));
      return [undefined, false];
    } catch (e) {
      if (!(e instanceof ResponseError)) {
        toast.error(t('Form.CommunicationErrorMessage'));
        return [undefined, true];
      }

      if (typeof e.errors === 'string') {
        toast.error(e.errors);
        return [undefined, true];
      }

      showValidationErrorToast(e.errors, t('ContentForm.DuplicationFailed'));
      return [e.errors, true];
    }
  };

  const onDuplicate = async (objectToDuplicate?: ContentObject | null) => {
    const contentTypeName = contentType?.name;
    const id = objectToDuplicate?.id;

    if (!contentTypeName || !id) {
      toast.error(t('ContentForm.DuplicateNoObject'));
      return;
    }

    const objectRelations = collectDuplicableRelations(
      objectToDuplicate,
      contentType?.metaDefinition?.propertiesConfig || {},
    );

    if (Object.keys(objectRelations).length === 0) {
      modal({
        id,
        title: t('ContentForm.Duplicate', { contentTypeName: '' }),
        content: (
          <div className="flex flex-col gap-4">
            <p>{t('ContentForm.Duplicating')}</p>
            <Loader
              size="small"
              type="spinner-grid"
              additionalClasses="mx-auto"
            />
          </div>
        ),
        hideClose: true,
        contentAdditionalClasses: 'pb-8',
      });
      await duplicateObject(contentTypeName, id, []);
      modal.close(id);
      return;
    }

    modal({
      title: t('ContentForm.Duplicate', { contentTypeName: '' }),
      content: (
        <DuplicateRelationsForm
          objectRelations={objectRelations}
          onSubmit={(dataUrlToSuplicate: string[]) =>
            duplicateObject(contentTypeName, id, dataUrlToSuplicate)
          }
          {...getTestProps(testId, 'duplicate-form', 'testId')}
        />
      ),
      size: 'lg',
    });
  };

  return { onDuplicate };
};
